/**
 * Utilidades de calendário.
 *
 * Datas são tratadas como strings ISO `YYYY-MM-DD` e convertidas sempre em UTC,
 * para não sofrer com fuso horário (um clássico de gerador de agenda).
 */
export const DIAS_CURTOS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
export const DIAS_LONGOS = [
    'domingo',
    'segunda-feira',
    'terça-feira',
    'quarta-feira',
    'quinta-feira',
    'sexta-feira',
    'sábado',
];
export const DIAS_MAIUSCULOS = [
    'DOMINGO',
    'SEGUNDA-FEIRA',
    'TERÇA-FEIRA',
    'QUARTA-FEIRA',
    'QUINTA-FEIRA',
    'SEXTA-FEIRA',
    'SÁBADO',
];
export const MESES = [
    'janeiro',
    'fevereiro',
    'março',
    'abril',
    'maio',
    'junho',
    'julho',
    'agosto',
    'setembro',
    'outubro',
    'novembro',
    'dezembro',
];
export const MESES_MAIUSCULOS = [
    'JANEIRO',
    'FEVEREIRO',
    'MARÇO',
    'ABRIL',
    'MAIO',
    'JUNHO',
    'JULHO',
    'AGOSTO',
    'SETEMBRO',
    'OUTUBRO',
    'NOVEMBRO',
    'DEZEMBRO',
];
export const MESES_ABREV = [
    'jan',
    'fev',
    'mar',
    'abr',
    'mai',
    'jun',
    'jul',
    'ago',
    'set',
    'out',
    'nov',
    'dez',
];
/**
 * Siglas de uma letra, de domingo a sábado.
 * Em agenda sem data, são impressas para o usuário circular o dia certo.
 */
export const SIGLAS_DIAS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
export function estaNoIntervalo(iso, inicio, fim) {
    return iso >= inicio && iso <= fim;
}
export function partesDe(iso) {
    const [ano, mes, dia] = iso.split('-').map((n) => Number.parseInt(n, 10));
    const data = new Date(Date.UTC(ano ?? 1970, (mes ?? 1) - 1, dia ?? 1));
    return {
        iso,
        ano: data.getUTCFullYear(),
        mes: data.getUTCMonth() + 1,
        dia: data.getUTCDate(),
        diaSemana: data.getUTCDay(),
    };
}
export function montarIso(ano, mes, dia) {
    const d = new Date(Date.UTC(ano, mes - 1, dia));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}
export function somarDias(iso, dias) {
    const { ano, mes, dia } = partesDe(iso);
    const d = new Date(Date.UTC(ano, mes - 1, dia + dias));
    return montarIso(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}
export function diferencaEmDias(inicio, fim) {
    const a = Date.parse(`${inicio}T00:00:00Z`);
    const b = Date.parse(`${fim}T00:00:00Z`);
    return Math.round((b - a) / 86400000);
}
/** Lista inclusiva de datas entre `inicio` e `fim`, com teto de segurança. */
export function listarDias(inicio, fim, teto = 1000) {
    const total = diferencaEmDias(inicio, fim);
    if (total < 0)
        return [];
    const limite = Math.min(total, teto);
    const dias = [];
    for (let i = 0; i <= limite; i += 1)
        dias.push(somarDias(inicio, i));
    return dias;
}
export function diasPermitidos(modo) {
    switch (modo) {
        case 'SEG_A_SEX':
            return [1, 2, 3, 4, 5];
        case 'SEG_A_SAB':
            return [1, 2, 3, 4, 5, 6];
        case 'SAB_E_DOM':
            return [0, 6];
        case 'SEG_A_DOM':
        default:
            return [0, 1, 2, 3, 4, 5, 6];
    }
}
export function nomeDiaSemana(diaSemana, formato = 'LONGO') {
    const indice = ((diaSemana % 7) + 7) % 7;
    if (formato === 'CURTO')
        return DIAS_CURTOS[indice];
    if (formato === 'MAIUSCULO')
        return DIAS_MAIUSCULOS[indice];
    return DIAS_LONGOS[indice];
}
export function nomeMes(mes, formato = 'LONGO') {
    const indice = (((mes - 1) % 12) + 12) % 12;
    if (formato === 'CURTO')
        return MESES_ABREV[indice];
    if (formato === 'MAIUSCULO')
        return MESES_MAIUSCULOS[indice];
    return MESES[indice];
}
export function formatarDataCurta(iso) {
    const { dia, mes, ano } = partesDe(iso);
    return `${String(dia).padStart(2, '0')}/${String(mes).padStart(2, '0')}/${ano}`;
}
export function formatarDataLonga(iso) {
    const { dia, mes, ano, diaSemana } = partesDe(iso);
    return `${nomeDiaSemana(diaSemana)}, ${dia} de ${nomeMes(mes)} de ${ano}`;
}
export function formatarMesAno(ano, mes) {
    return `${nomeMes(mes)} de ${ano}`;
}
export function primeiroDiaDoAno(ano) {
    return montarIso(ano, 1, 1);
}
export function ultimoDiaDoAno(ano) {
    return montarIso(ano, 12, 31);
}
export function diasNoMes(ano, mes) {
    return new Date(Date.UTC(ano, mes, 0)).getUTCDate();
}
/** Meses (1-12) tocados por um intervalo, agrupados por ano. */
export function mesesDoIntervalo(inicio, fim) {
    const a = partesDe(inicio);
    const b = partesDe(fim);
    const meses = [];
    let ano = a.ano;
    let mes = a.mes;
    let guarda = 0;
    while ((ano < b.ano || (ano === b.ano && mes <= b.mes)) && guarda < 240) {
        meses.push({ ano, mes });
        mes += 1;
        if (mes > 12) {
            mes = 1;
            ano += 1;
        }
        guarda += 1;
    }
    return meses;
}
/**
 * Matriz do mês: linhas = semanas, colunas = domingo..sábado.
 * Dias fora do mês vêm como `null`.
 */
export function matrizDoMes(ano, mes) {
    const total = diasNoMes(ano, mes);
    const primeiro = montarIso(ano, mes, 1);
    const deslocamento = partesDe(primeiro).diaSemana;
    const celulas = Array.from({ length: deslocamento }, () => null);
    for (let dia = 1; dia <= total; dia += 1)
        celulas.push(montarIso(ano, mes, dia));
    while (celulas.length % 7 !== 0)
        celulas.push(null);
    const semanas = [];
    for (let i = 0; i < celulas.length; i += 7)
        semanas.push(celulas.slice(i, i + 7));
    return semanas;
}
/** Número ISO da semana (1-53). */
export function numeroSemana(iso) {
    const { ano, mes, dia } = partesDe(iso);
    const data = new Date(Date.UTC(ano, mes - 1, dia));
    const diaDaSemana = (data.getUTCDay() + 6) % 7; // segunda = 0
    data.setUTCDate(data.getUTCDate() - diaDaSemana + 3);
    const primeiraQuinta = new Date(Date.UTC(data.getUTCFullYear(), 0, 4));
    const diff = data.getTime() - primeiraQuinta.getTime();
    return 1 + Math.round(diff / (7 * 86400000));
}
export function trimestreDoMes(mes) {
    return Math.ceil(mes / 3);
}
export function anoAtual() {
    return new Date().getUTCFullYear();
}
export function hojeIso() {
    const agora = new Date();
    return montarIso(agora.getFullYear(), agora.getMonth() + 1, agora.getDate());
}
/** Rótulo do período para exibir em capas e resumos. */
export function rotuloPeriodo(inicio, fim) {
    const a = partesDe(inicio);
    const b = partesDe(fim);
    if (a.ano === b.ano && a.mes === b.mes)
        return `${formatarDataCurta(inicio)} a ${formatarDataCurta(fim)}`;
    if (a.ano === b.ano) {
        return `${nomeMes(a.mes)} a ${nomeMes(b.mes)} de ${a.ano}`;
    }
    return `${formatarDataCurta(inicio)} a ${formatarDataCurta(fim)}`;
}
//# sourceMappingURL=datas.js.map